/** API client for the Flask backend. */

const BASE = process.env.REACT_APP_API_URL || "";
const OPTS = { credentials: "include" };

async function api(url, opts = {}) {
  const resp = await fetch(url, { ...OPTS, ...opts });
  if (resp.status === 401) throw new Error("Unauthorized");
  if (!resp.ok) {
    const body = await resp.json().catch(() => null);
    throw new Error(body?.error || `API error: ${resp.status}`);
  }
  return resp.json();
}

// ── Auth ──────────────────────────────────────────────────────────

export async function login(username, password) {
  return api(`${BASE}/api/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
}

export async function logout() {
  return api(`${BASE}/api/logout`, { method: "POST" });
}

export async function checkAuth() {
  return api(`${BASE}/api/me`);
}

export async function checkMicrosoftEnabled() {
  return api(`${BASE}/api/auth/microsoft/enabled`);
}

// ── Data ──────────────────────────────────────────────────────────

export async function fetchCves({ page = 0, perPage = 50, search, year } = {}) {
  const params = new URLSearchParams({ page, per_page: perPage });
  if (search) params.set("search", search);
  if (year) params.set("year", year);
  return api(`${BASE}/api/cves?${params}`);
}

export async function fetchCve(cveId) {
  return api(`${BASE}/api/cves/${cveId}`);
}

export async function fetchProducts({ page = 0, perPage = 50, search, scored } = {}) {
  const params = new URLSearchParams({ page, per_page: perPage });
  if (search) params.set("search", search);
  if (scored) params.set("scored", scored);
  return api(`${BASE}/api/products?${params}`);
}

export async function fetchProductsByCna() {
  return api(`${BASE}/api/products/by-cna`);
}

export async function downloadCsaf(cveId) {
  const resp = await fetch(`${BASE}/api/cves/${cveId}/csaf`, OPTS);
  if (resp.status === 401) throw new Error("Unauthorized");
  if (!resp.ok) {
    const body = await resp.json().catch(() => null);
    throw new Error(body?.error || `CSAF export failed: ${resp.status}`);
  }
  const blob = await resp.blob();
  const cd = resp.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="?([^";]+)"?/);
  const filename = m ? m[1] : `vinnie-${cveId.toLowerCase()}.json`;
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function fetchScoreHealth(days = 5) {
  return api(`${BASE}/api/health/scores?days=${days}`);
}

export async function fetchSpendHistory(days = 30) {
  return api(`${BASE}/api/spend/history?days=${days}`);
}

export async function fetchPipelineQueue() {
  return api(`${BASE}/api/pipeline/queue`);
}

export async function fetchPipelineSync() {
  return api(`${BASE}/api/pipeline/sync`);
}

export async function fetchPipelineScoring() {
  return api(`${BASE}/api/pipeline/scoring`);
}

export async function fetchPipelineCpe() {
  return api(`${BASE}/api/pipeline/cpe`);
}

export async function fetchPipelinePrevalence() {
  return api(`${BASE}/api/pipeline/prevalence`);
}

export async function fetchPipelineImport() {
  return api(`${BASE}/api/pipeline/import`);
}

export async function fetchPipelineRefs() {
  return api(`${BASE}/api/pipeline/refs`);
}

export async function fetchPipelineTagging() {
  return api(`${BASE}/api/pipeline/tagging`);
}

export async function fetchProductIngestion(days = 14) {
  return api(`${BASE}/api/health/products?days=${days}`);
}

export async function fetchScoreAgreement(days = 5) {
  return api(`${BASE}/api/health/agreement?days=${days}`);
}

export async function fetchCnaAgreement(cna, days = 5) {
  return api(`${BASE}/api/health/agreement/${encodeURIComponent(cna)}?days=${days}`);
}

export async function fetchRecent(limit = 10) {
  return api(`${BASE}/api/recent?limit=${limit}`);
}

export async function fetchRecentBuckets(limit = 15) {
  return api(`${BASE}/api/recent-buckets?limit=${limit}`);
}

export async function fetchCvePrevalence(cveId) {
  return api(`${BASE}/api/cves/${cveId}/prevalence`);
}

// Queries tab — prevalence rollup for a recent window.
export async function fetchRecentPrevalence({ days = 1, kind = "new", limit = 100, scoredOnly = false } = {}) {
  const qs = new URLSearchParams({ days, kind, limit });
  if (scoredOnly) qs.set("scored_only", "1");
  return api(`${BASE}/api/queries/recent-prevalence?${qs}`);
}

// Same query, streamed as CSV and NOT capped at the page limit — the server
// walks the whole window off a cursor. Buffered here as a blob because a
// plain navigation would drop the session cookie handling `api()` relies on,
// and because the caller wants to show progress while it runs.
export async function downloadRecentPrevalenceCsv(
  { days = 1, kind = "new", scoredOnly = false, view = "summary" } = {}
) {
  const qs = new URLSearchParams({ days, kind, view });
  if (scoredOnly) qs.set("scored_only", "1");
  const resp = await fetch(`${BASE}/api/queries/recent-prevalence.csv?${qs}`, OPTS);
  if (resp.status === 401) throw new Error("Unauthorized");
  if (!resp.ok) {
    const body = await resp.json().catch(() => null);
    throw new Error(body?.error || `Export failed: ${resp.status}`);
  }
  const blob = await resp.blob();
  const cd = resp.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="?([^";]+)"?/);
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), {
    href: url, download: m ? m[1] : `recent-prevalence-${view}.csv`,
  });
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return blob.size;
}

export async function fetchSimilar(cveId, k = 5) {
  return api(`${BASE}/api/similar/${cveId}?k=${k}`);
}

export async function flushCache() {
  return api(`${BASE}/api/flush`, { method: "POST" });
}

export async function fetchRefContent(hash) {
  return api(`${BASE}/api/refs/${hash}`);
}

export async function analyzeCve(cveId) {
  return api(`${BASE}/api/cves/${cveId}/analyze`, { method: "POST" });
}

export async function analyzeStatus(cveId) {
  return api(`${BASE}/api/cves/${cveId}/analyze/status`);
}

export async function analyzeLlama(cveId) {
  return api(`${BASE}/api/cves/${cveId}/analyze-llama`, { method: "POST" });
}

export async function analyzeLlamaStatus(cveId) {
  return api(`${BASE}/api/cves/${cveId}/analyze-llama/status`);
}

export async function analyzeComposite(cveId) {
  return api(`${BASE}/api/cves/${cveId}/analyze-composite`, { method: "POST" });
}

export async function analyzeCompositeStatus(cveId) {
  return api(`${BASE}/api/cves/${cveId}/analyze-composite/status`);
}

export async function fetchNvdScores(cveId) {
  return api(`${BASE}/api/cves/${cveId}/fetch-nvd`, { method: "POST" });
}

export async function fetchJobs() {
  return api(`${BASE}/api/jobs`);
}

export async function deleteJob(cveId) {
  return api(`${BASE}/api/jobs/${cveId}`, { method: "DELETE" });
}

export async function refreshRef(hash, url) {
  return api(`${BASE}/api/refs/${hash}/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
  });
}

export async function refreshRepos() {
  return api(`${BASE}/api/refresh-repos`, { method: "POST" });
}

export async function importCve(cveId) {
  return api(`${BASE}/api/import-cve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cve_id: cveId }),
  });
}

export async function fetchComparisons() {
  return api(`${BASE}/api/comparisons`);
}

export async function fetchComparison(name) {
  return api(`${BASE}/api/comparisons/${name}`);
}

export async function fetchArtieJobs() {
  return api(`${BASE}/api/artie/jobs`);
}

export async function fetchArtieJob(jobId) {
  return api(`${BASE}/api/artie/jobs/${jobId}`);
}

export async function fetchNewsList() {
  return api(`${BASE}/api/news`);
}

export async function fetchNewsReport(filename) {
  return api(`${BASE}/api/news/${filename}`);
}

export async function deleteNewsReport(filename) {
  return api(`${BASE}/api/news/${filename}`, { method: "DELETE" });
}

// ── CVSS human review ─────────────────────────────────────────────

function reviewParams(filters = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && !v.length)) {
      qs.set(k, Array.isArray(v) ? v.join(",") : v);
    }
  }
  return qs;
}

export async function fetchReviewQueue(filters = {}, page = 0, perPage = 50) {
  const qs = reviewParams(filters);
  qs.set("page", page);
  qs.set("per_page", perPage);
  return api(`${BASE}/api/review/queue?${qs}`);
}

export async function fetchReview(cveId, filters = {}) {
  return api(`${BASE}/api/review/${cveId}?${reviewParams(filters)}`);
}

export async function lockReview(cveId, type = "cvss") {
  const resp = await fetch(`${BASE}/api/review/${cveId}/lock?type=${type}`, { ...OPTS, method: "POST" });
  if (resp.status === 401) throw new Error("Unauthorized");
  return resp.json(); // 409 = someone else has it open; the body says who
}

export async function saveReviewDecision(cveId, body) {
  const resp = await fetch(`${BASE}/api/review/${cveId}/decision`, {
    ...OPTS,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (resp.status === 401) throw new Error("Unauthorized");
  const data = await resp.json().catch(() => ({}));
  return { status: resp.status, ...data }; // 409 = reviewed by someone else since it was loaded
}

export async function fetchReviewRules(type = "cvss") {
  return api(`${BASE}/api/review/rules?type=${type}`);
}

export async function fetchReviewFeedback(type = "cvss") {
  return api(`${BASE}/api/review/feedback?type=${type}`);
}

export async function downloadReviewCsv(filters = {}) {
  const resp = await fetch(`${BASE}/api/review/queue.csv?${reviewParams(filters)}`, OPTS);
  if (resp.status === 401) throw new Error("Unauthorized");
  if (!resp.ok) throw new Error(`Export failed: ${resp.status}`);
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: `${filters.type || "cvss"}-review-queue.csv` });
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
